/**
 * Security Utilities
 *
 * Comprehensive security functions for input validation, sanitization,
 * XSS prevention, SQL injection protection, and secure data handling.
 *
 * Security Features:
 * - Input sanitization (allowlist approach)
 * - XSS prevention and output encoding
 * - SQL injection prevention helpers
 * - CSRF token management
 * - Secure data validation
 * - Content Security Policy helpers
 *
 * @module utils/security
 */

/**
 * HTML entity encode map
 * SECURITY: Used to prevent XSS attacks
 */
const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
};

/**
 * Escape HTML to prevent XSS attacks
 * SECURITY: Converts dangerous characters to HTML entities
 * @param {string} str - String to escape
 * @returns {string} Escaped string safe for HTML output
 */
export const escapeHtml = (str) => {
  if (typeof str !== 'string') {
    return '';
  }

  return str.replace(/[&<>"'/]/g, (char) => HTML_ENTITIES[char] || char);
};

/**
 * Sanitize text input
 * SECURITY: Remove dangerous characters while preserving readable text
 * @param {string} input - User input to sanitize
 * @param {Object} options - Sanitization options
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input, options = {}) => {
  if (typeof input !== 'string') {
    return '';
  }

  const {
    allowHtml = false,
    maxLength = 10000,
    allowNewlines = true,
  } = options;

  let sanitized = input;

  // SECURITY: Remove null bytes
  sanitized = sanitized.replace(/\0/g, '');

  // SECURITY: Trim whitespace
  sanitized = sanitized.trim();

  // SECURITY: Enforce maximum length (prevent DoS)
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  // SECURITY: Remove or escape HTML based on options
  if (!allowHtml) {
    sanitized = escapeHtml(sanitized);
  }

  // SECURITY: Normalize newlines
  if (!allowNewlines) {
    sanitized = sanitized.replace(/[\r\n]+/g, ' ');
  }

  return sanitized;
};

/**
 * Validate and sanitize email address
 * SECURITY: Prevent email injection and validate format
 * @param {string} email - Email to validate
 * @returns {Object} Validation result
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return {
      valid: false,
      sanitized: '',
      error: 'Email is required',
    };
  }

  // SECURITY: Trim and lowercase
  const sanitized = email.trim().toLowerCase();

  // SECURITY: Validate email format (RFC 5322 simplified)
  const emailRegex = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

  if (!emailRegex.test(sanitized)) {
    return {
      valid: false,
      sanitized,
      error: 'Invalid email format',
    };
  }

  // SECURITY: Check length limits
  if (sanitized.length > 254) {
    return {
      valid: false,
      sanitized,
      error: 'Email too long',
    };
  }

  // SECURITY: Check for dangerous patterns
  if (sanitized.includes('..') || sanitized.includes('@.')) {
    return {
      valid: false,
      sanitized,
      error: 'Invalid email format',
    };
  }

  return {
    valid: true,
    sanitized,
    error: null,
  };
};

/**
 * Validate and sanitize phone number
 * SECURITY: Remove dangerous characters and validate format
 * @param {string} phone - Phone number to validate
 * @returns {Object} Validation result
 */
export const validatePhone = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return {
      valid: false,
      sanitized: '',
      error: 'Phone number is required',
    };
  }

  // SECURITY: Remove all non-numeric characters except + - ( ) and spaces
  let sanitized = phone.replace(/[^0-9+\-() ]/g, '');
  sanitized = sanitized.trim();

  // SECURITY: Validate length (international format: 7-15 digits)
  const digitsOnly = sanitized.replace(/[^0-9]/g, '');

  if (digitsOnly.length < 7 || digitsOnly.length > 15) {
    return {
      valid: false,
      sanitized,
      error: 'Invalid phone number length',
    };
  }

  return {
    valid: true,
    sanitized,
    error: null,
  };
};

/**
 * Validate and sanitize URL
 * SECURITY: Prevent javascript: and data: URLs, ensure HTTPS
 * @param {string} url - URL to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result
 */
export const validateUrl = (url, options = {}) => {
  const {
    requireHttps = true,
    allowedDomains = null,
  } = options;

  if (!url || typeof url !== 'string') {
    return {
      valid: false,
      sanitized: '',
      error: 'URL is required',
    };
  }

  // SECURITY: Trim whitespace
  const sanitized = url.trim();

  try {
    const urlObj = new URL(sanitized);

    // SECURITY: Prevent dangerous protocols
    const allowedProtocols = requireHttps ? ['https:'] : ['http:', 'https:'];

    if (!allowedProtocols.includes(urlObj.protocol)) {
      return {
        valid: false,
        sanitized,
        error: 'Invalid URL protocol',
      };
    }

    // SECURITY: Check allowed domains if specified
    if (allowedDomains && !allowedDomains.includes(urlObj.hostname)) {
      return {
        valid: false,
        sanitized,
        error: 'Domain not allowed',
      };
    }

    return {
      valid: true,
      sanitized,
      error: null,
    };
  } catch (error) {
    return {
      valid: false,
      sanitized,
      error: 'Invalid URL format',
    };
  }
};

/**
 * Sanitize object for database insertion
 * SECURITY: Remove undefined, null, and dangerous values
 * @param {Object} obj - Object to sanitize
 * @param {Array} allowedKeys - Allowed keys (allowlist)
 * @returns {Object} Sanitized object
 */
export const sanitizeObject = (obj, allowedKeys = null) => {
  if (!obj || typeof obj !== 'object') {
    return {};
  }

  const sanitized = {};

  Object.keys(obj).forEach((key) => {
    // SECURITY: Allowlist check
    if (allowedKeys && !allowedKeys.includes(key)) {
      return;
    }

    const value = obj[key];

    // SECURITY: Skip undefined and null
    if (value === undefined || value === null) {
      return;
    }

    // SECURITY: Sanitize strings
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    }
    // SECURITY: Preserve numbers, booleans, dates
    else if (typeof value === 'number' || typeof value === 'boolean' || value instanceof Date) {
      sanitized[key] = value;
    }
    // SECURITY: Recursively sanitize objects
    else if (typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeObject(value);
    }
    // SECURITY: Sanitize arrays
    else if (Array.isArray(value)) {
      sanitized[key] = value.map(item =>
        typeof item === 'string' ? sanitizeInput(item) : item
      );
    }
  });

  return sanitized;
};

/**
 * Validate SQL-like input to prevent injection
 * SECURITY: Detect and reject dangerous SQL patterns
 * @param {string} input - Input to validate
 * @returns {Object} Validation result
 */
export const validateSqlInput = (input) => {
  if (typeof input !== 'string') {
    return { valid: true, error: null };
  }

  // SECURITY: Dangerous SQL patterns
  const dangerousPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|DECLARE)\b)/gi,
    /(--|;|\/\*|\*\/|xp_|sp_)/gi,
    /(\bOR\b.*=.*|'\s*OR\s*')/gi,
    /(\bAND\b.*=.*)/gi,
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(input)) {
      return {
        valid: false,
        error: 'Input contains potentially dangerous content',
      };
    }
  }

  return { valid: true, error: null };
};

/**
 * Generate CSRF token
 * SECURITY: Cryptographically secure random token
 * @returns {string} CSRF token
 */
export const generateCsrfToken = () => {
  // SECURITY: Use crypto.randomUUID for secure random generation
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback to random string
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

/**
 * CSRF token storage and validation
 */
export const csrfToken = {
  /**
   * Get CSRF token from storage or generate new
   * @returns {string} CSRF token
   */
  get() {
    let token = sessionStorage.getItem('csrf_token');

    if (!token) {
      token = generateCsrfToken();
      this.set(token);
    }

    return token;
  },

  /**
   * Set CSRF token in storage
   * @param {string} token - CSRF token
   */
  set(token) {
    sessionStorage.setItem('csrf_token', token);
  },

  /**
   * Validate CSRF token
   * @param {string} token - Token to validate
   * @returns {boolean} True if valid
   */
  validate(token) {
    const storedToken = this.get();
    return token === storedToken;
  },

  /**
   * Clear CSRF token
   */
  clear() {
    sessionStorage.removeItem('csrf_token');
  },
};

/**
 * Validate numeric input
 * SECURITY: Ensure input is valid number within range
 * @param {any} value - Value to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result
 */
export const validateNumber = (value, options = {}) => {
  const {
    min = -Infinity,
    max = Infinity,
    integer = false,
  } = options;

  const num = Number(value);

  if (isNaN(num) || !isFinite(num)) {
    return {
      valid: false,
      value: 0,
      error: 'Invalid number',
    };
  }

  if (integer && !Number.isInteger(num)) {
    return {
      valid: false,
      value: Math.round(num),
      error: 'Must be an integer',
    };
  }

  if (num < min || num > max) {
    return {
      valid: false,
      value: Math.max(min, Math.min(max, num)),
      error: `Number must be between ${min} and ${max}`,
    };
  }

  return {
    valid: true,
    value: num,
    error: null,
  };
};

/**
 * Validate date input
 * SECURITY: Ensure valid date format
 * @param {any} value - Date value to validate
 * @returns {Object} Validation result
 */
export const validateDate = (value) => {
  if (!value) {
    return {
      valid: false,
      date: null,
      error: 'Date is required',
    };
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return {
      valid: false,
      date: null,
      error: 'Invalid date format',
    };
  }

  // SECURITY: Check for reasonable date range (1900-2100)
  const year = date.getFullYear();
  if (year < 1900 || year > 2100) {
    return {
      valid: false,
      date,
      error: 'Date out of valid range',
    };
  }

  return {
    valid: true,
    date,
    error: null,
  };
};

/**
 * Rate limiter for client-side operations
 * SECURITY: Prevent abuse of operations
 */
export class RateLimiter {
  constructor(maxAttempts = 5, windowMs = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
    this.attempts = new Map();
  }

  /**
   * Check if operation is rate limited
   * @param {string} key - Unique key for operation
   * @returns {boolean} True if rate limited
   */
  isLimited(key) {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];

    // Clean old attempts
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);

    this.attempts.set(key, recentAttempts);

    return recentAttempts.length >= this.maxAttempts;
  }

  /**
   * Record an attempt
   * @param {string} key - Unique key for operation
   */
  recordAttempt(key) {
    const attempts = this.attempts.get(key) || [];
    attempts.push(Date.now());
    this.attempts.set(key, attempts);
  }

  /**
   * Clear attempts for key
   * @param {string} key - Unique key for operation
   */
  clear(key) {
    this.attempts.delete(key);
  }
}

/**
 * Secure localStorage wrapper
 * SECURITY: Prevent XSS access to sensitive data
 */
export const secureStorage = {
  /**
   * Get item from storage
   * @param {string} key - Storage key
   * @returns {any} Stored value
   */
  get(key) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('[STORAGE] Get error:', error.message);
      return null;
    }
  },

  /**
   * Set item in storage
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   */
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('[STORAGE] Set error:', error.message);
    }
  },

  /**
   * Remove item from storage
   * @param {string} key - Storage key
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('[STORAGE] Remove error:', error.message);
    }
  },

  /**
   * Clear all storage
   */
  clear() {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('[STORAGE] Clear error:', error.message);
    }
  },
};

const security = {
  escapeHtml,
  sanitizeInput,
  validateEmail,
  validatePhone,
  validateUrl,
  sanitizeObject,
  validateSqlInput,
  generateCsrfToken,
  csrfToken,
  validateNumber,
  validateDate,
  RateLimiter,
  secureStorage,
};

export default security;
